'use client';

import React from 'react';

import { useLocale } from 'next-intl';

import { WindowQuantityStepper } from '@/components/services/window-cleaning/WindowQuantityStepper';
import { WindowTypeIcon } from '@/components/services/window-cleaning/WindowTypeIcon';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { NumberStepper } from '@/components/ui/number-stepper';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  WINDOW_TYPE_IDS,
  type WindowPricingMode,
  type WindowTypeId,
  type WindowTypeQuantities,
} from '@/lib/pricing/window-cleaning-calculator';
import { cn } from '@/lib/utils';

export interface WindowCleaningConfiguratorLabels {
  modeType: string;
  modeArea: string;
  areaHint: string;
  areaWindowLabel: string;
  areaBlindsLabel: string;
  windowsCount: (count: number) => string;
  blindsCount: (count: number) => string;
  afterRenovation: string;
  addOnsTitle: string;
  commentLabel: string;
  commentPlaceholder: string;
  typeTitles: Record<WindowTypeId, string>;
}

export interface WindowCleaningConfiguratorProps {
  labels: WindowCleaningConfiguratorLabels;
  mode: WindowPricingMode;
  onModeChange: (mode: WindowPricingMode) => void;
  types: WindowTypeQuantities;
  onTypeChange: (id: WindowTypeId, field: 'windows' | 'blinds', value: number) => void;
  windowSqm: number;
  onWindowSqmChange: (value: number) => void;
  blindsSqm: number;
  onBlindsSqmChange: (value: number) => void;
  afterRenovation: boolean;
  onAfterRenovationChange: (checked: boolean) => void;
  /** Add-on cards for the current surface. */
  addOns?: React.ReactNode;
  comment?: string;
  onCommentChange?: (value: string) => void;
  className?: string;
}

/**
 * Window-cleaning configurator: type vs. area pricing, blinds, post-renovation
 * dust, optional add-ons and a note field.
 *
 * Fully controlled and copy-agnostic so the marketing calculator and the
 * booking flow can share one layout without sharing translations.
 */
export function WindowCleaningConfigurator({
  labels,
  mode,
  onModeChange,
  types,
  onTypeChange,
  windowSqm,
  onWindowSqmChange,
  blindsSqm,
  onBlindsSqmChange,
  afterRenovation,
  onAfterRenovationChange,
  addOns,
  comment,
  onCommentChange,
  className,
}: WindowCleaningConfiguratorProps) {
  const locale = useLocale();
  const intlLocale = locale === 'bg' ? 'bg-BG' : locale === 'de' ? 'de-DE' : 'en-GB';
  const formatAreaValue = (value: number) =>
    new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 2 }).format(value);

  return (
    <div className={cn('space-y-6', className)}>
      <Tabs
        value={mode}
        onValueChange={value => onModeChange(value as WindowPricingMode)}
        className='w-full'
      >
        <TabsList
          variant='underline'
          className='mx-auto mb-2 flex w-full max-w-xs justify-center gap-6 border-b-0'
        >
          <TabsTrigger
            variant='underline'
            value='type'
            className='flex-none border-b-2 border-transparent px-0 py-3 text-sm font-bold uppercase tracking-wide text-oxford-blue/45 data-[state=active]:border-oxford-blue data-[state=active]:text-oxford-blue'
          >
            {labels.modeType}
          </TabsTrigger>
          <TabsTrigger
            variant='underline'
            value='area'
            className='flex-none border-b-2 border-transparent px-0 py-3 text-sm font-bold uppercase tracking-wide text-oxford-blue/45 data-[state=active]:border-oxford-blue data-[state=active]:text-oxford-blue'
          >
            {labels.modeArea}
          </TabsTrigger>
        </TabsList>

        <TabsContent value='type' className='mt-0 space-y-0 outline-none'>
          {WINDOW_TYPE_IDS.map((id, index) => (
            <div
              key={id}
              className={cn('space-y-3 py-5', index > 0 && 'border-t border-oxford-blue/8')}
            >
              <h3 className='text-left text-sm font-bold uppercase tracking-[0.08em] text-oxford-blue'>
                {labels.typeTitles[id]}
              </h3>
              <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5'>
                <div className='flex justify-start sm:w-[12.5rem] sm:shrink-0'>
                  <WindowTypeIcon type={id} />
                </div>
                <div className='flex min-w-0 flex-1 flex-col gap-2.5 sm:max-w-md'>
                  <WindowQuantityStepper
                    label={labels.windowsCount(types[id].windows)}
                    value={types[id].windows}
                    onChange={value => onTypeChange(id, 'windows', value)}
                  />
                  <WindowQuantityStepper
                    label={labels.blindsCount(types[id].blinds)}
                    value={types[id].blinds}
                    onChange={value => onTypeChange(id, 'blinds', value)}
                    max={types[id].windows}
                    disabled={types[id].windows === 0}
                  />
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value='area' className='mt-0 space-y-4 outline-none'>
          <p className='text-base font-semibold leading-relaxed text-oxford-blue/80'>
            {labels.areaHint}
          </p>
          <div className='grid gap-3 sm:grid-cols-2'>
            <NumberStepper
              label={labels.areaWindowLabel}
              value={windowSqm}
              // One write per field: the parent owns the "blinds never exceed the
              // window area" clamp. Writing both here would clobber the new
              // window area with a stale value.
              onChange={value => onWindowSqmChange(Math.min(500, Math.max(0, value)))}
              min={0}
              max={500}
              step={0.5}
              unit='m²'
              variant='orange'
              formatValue={formatAreaValue}
            />
            <NumberStepper
              label={labels.areaBlindsLabel}
              value={blindsSqm}
              onChange={value => onBlindsSqmChange(Math.min(windowSqm || 500, Math.max(0, value)))}
              min={0}
              max={windowSqm || 500}
              step={0.5}
              unit='m²'
              variant='orange'
              formatValue={formatAreaValue}
            />
          </div>
        </TabsContent>
      </Tabs>

      <label className='flex w-full cursor-pointer items-center gap-3 py-1 text-left'>
        <Checkbox
          variant='default'
          size='md'
          checked={afterRenovation}
          onCheckedChange={checked => onAfterRenovationChange(checked === true)}
          aria-label={labels.afterRenovation}
          className='h-5 w-5 border-2 border-oxford-blue/40 data-[state=checked]:border-orange-peel data-[state=checked]:text-orange-peel'
        />
        <span className='text-sm font-semibold text-oxford-blue sm:text-base'>
          {labels.afterRenovation}
        </span>
      </label>

      {addOns ? (
        <div>
          <div className='mb-4 flex items-center gap-3'>
            <div className='h-px flex-1 border-t border-dashed border-oxford-blue/20' />
            <h3 className='shrink-0 text-xs font-bold uppercase tracking-[0.14em] text-oxford-blue/55'>
              {labels.addOnsTitle}
            </h3>
            <div className='h-px flex-1 border-t border-dashed border-oxford-blue/20' />
          </div>
          {addOns}
        </div>
      ) : null}

      {onCommentChange ? (
        <div>
          <div className='mb-3 flex items-center gap-3'>
            <div className='h-px flex-1 border-t border-dashed border-oxford-blue/20' />
            <Label
              htmlFor='window-comment'
              className='shrink-0 text-xs font-bold uppercase tracking-[0.14em] text-oxford-blue/55'
            >
              {labels.commentLabel}
            </Label>
            <div className='h-px flex-1 border-t border-dashed border-oxford-blue/20' />
          </div>
          <textarea
            id='window-comment'
            value={comment ?? ''}
            onChange={event => onCommentChange(event.target.value.slice(0, 280))}
            placeholder={labels.commentPlaceholder}
            rows={3}
            className='w-full resize-y rounded-xl border-2 border-oxford-blue/12 bg-white px-4 py-3 text-sm text-oxford-blue placeholder:text-oxford-blue/40 focus-visible:border-oxford-blue/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-peel/30'
          />
        </div>
      ) : null}
    </div>
  );
}
